import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { NotesRounded, DeleteOutlineRounded } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import {
  MESSAGES,
  ORDER_STATUS,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const headerTitleSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  color: "#fff",
  fontWeight: 700,
};

// ─────────── NOT DIALOG ───────────
export function NoteDialog({ open, toStatus, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNote("");
      setError("");
    }
  }, [open]);

  const isPending = toStatus === ORDER_STATUS.PENDING;

  const handleSave = () => {
    const t = note.trim();
    if (t.length <= 5) {
      setError(MESSAGES.SUPPORTER_NOTE_MIN_ERROR);
      return;
    }
    onConfirm(t);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: "linear-gradient(135deg, #1A237E 0%, #045781 100%)",
        }}
      >
        <DialogTitle sx={{ ...headerTitleSx, p: 0 }}>
          <NotesRounded sx={{ fontSize: 20 }} />
          {isPending
            ? MESSAGES.SUPPORTER_NOTE_TITLE_PENDING
            : MESSAGES.SUPPORTER_NOTE_TITLE_DONE}
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <TextField
          fullWidth
          autoFocus
          multiline
          minRows={3}
          maxRows={6}
          label={
            isPending
              ? MESSAGES.SUPPORTER_NOTE_HINT_PENDING
              : MESSAGES.SUPPORTER_NOTE_HINT_DONE
          }
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (error) setError("");
          }}
          error={!!error}
          helperText={error}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            transition: "transform 0.2s ease",
            "&:hover": { transform: "translateY(-1px)" },
          }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            boxShadow: buttonShadow,
            "&:hover": {
              background: buttonGradientHover,
              transform: "translateY(-1px)",
            },
            "&:active": { transform: "translateY(0) scale(0.98)" },
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────── CANCEL DIALOG ───────────
export function CancelDialog({ open, order, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setNote("");
      setError("");
    }
  }, [open]);

  const handleCancel = () => {
    if (!note.trim()) {
      setError(MESSAGES.SUPPORTER_CANCEL_REQUIRED);
      return;
    }
    onConfirm(note.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: "linear-gradient(135deg, #B71C1C 0%, #7F1D1D 100%)",
        }}
      >
        <DialogTitle sx={{ ...headerTitleSx, p: 0 }}>
          <DeleteOutlineRounded sx={{ fontSize: 20 }} />
          {MESSAGES.SUPPORTER_CANCEL_TITLE}
        </DialogTitle>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 1.25,
          backgroundColor: "#FEF2F2",
          borderBottom: "1px solid #FEE2E2",
        }}
      >
        <Typography
          sx={{ fontSize: 12.5, color: "#991B1B", fontWeight: 600 }}
          noWrap
        >
          {order?.title || "-"}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <TextField
          fullWidth
          autoFocus
          multiline
          minRows={2}
          maxRows={5}
          label={MESSAGES.SUPPORTER_CANCEL_HINT}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (error) setError("");
          }}
          error={!!error}
          helperText={error}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            transition: "transform 0.2s ease",
            "&:hover": { transform: "translateY(-1px)" },
          }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            backgroundColor: "#B71C1C",
            "&:hover": {
              backgroundColor: "#991B1B",
              transform: "translateY(-1px)",
            },
            "&:active": { transform: "translateY(0) scale(0.98)" },
          }}
        >
          İptal Et
        </Button>
      </DialogActions>
    </Dialog>
  );
}
